import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { LuPlane } from 'react-icons/lu';

// ── Styles ──────────────────────────────────────────
import './styles/global.css';
import './styles/components.css';
import './styles/pages.css';

// ── Layout ──────────────────────────────────────────
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';

// ── Pages ────────────────────────────────────────────
import Home          from './pages/Home';
import HowItWorks    from './pages/HowItWorks';
import Technology    from './pages/Technology';
import Safety        from './pages/Safety';
import Types         from './pages/Types';
import Contact       from './pages/Contact';
import Privacy       from './pages/Privacy';
import Terms         from './pages/Terms';
import Accessibility from './pages/Accessibility';
import Newsletter    from './pages/Newsletter';
import Brochure      from './pages/Brochure';
import AdminWorkspace from './admin/pages/AdminWorkspacePage';
import AdminLogin from './admin/pages/AdminLoginPage';
import AdminToastProvider, { useAdminToast } from './admin/components/AdminToastProvider';
import {
  authenticateAdmin, clearAdminToken, clearAdminUser,
  readAdminToken, readAdminUser, saveAdminToken, saveAdminUser,
} from './admin/services/adminApi';

// ── Reveal hook ──────────────────────────────────────
import useReveal from './hooks/useReveal';

/** Re-triggers scroll-reveal observer on every route change */
function RevealController() {
  const { pathname } = useLocation();
  useReveal([pathname]);
  return null;
}

/** Scrolls to top (or hash target) on every navigation */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Small delay so the page renders before we scroll
      const id = setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
      return () => clearTimeout(id);
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [pathname, hash]);
  return null;
}

function AppContent() {
  const { pathname } = useLocation();
  const notify = useAdminToast();
  const isAdminRoute = pathname.startsWith('/admin');
  const [authToken, setAuthToken] = useState(readAdminToken);
  const [adminUser, setAdminUser] = useState(readAdminUser);

  const authenticate = useCallback(async (mode, credentials) => {
    const result = await authenticateAdmin(mode, credentials);
    saveAdminToken(result.token);
    saveAdminUser(result.user);
    setAuthToken(result.token);
    setAdminUser(result.user);
    notify(`Welcome, ${result.user?.name || 'Administrator'}.`, 'success');
  }, [notify]);

  const logoutAdmin = useCallback(errorMessage => {
    const message = typeof errorMessage === 'string' ? errorMessage : '';
    try {
      clearAdminToken();
      clearAdminUser();
      setAuthToken('');
      setAdminUser(null);
      if (message) notify(message, 'error');
    } catch (error) {
      console.error('Unable to clear admin token.', error);
      notify('Logout could not be completed. Please check browser storage and try again.', 'error');
    }
  }, [notify]);

  return (
    <>
      <ScrollToTop />
      <RevealController />
      {!isAdminRoute && <Navbar />}
      <main>
        <Routes>
          <Route path="/admin/*" element={authToken
            ? <AdminWorkspace authToken={authToken} user={adminUser} onLogout={logoutAdmin} />
              : <AdminLogin onAuthenticate={authenticate} />} />
          <Route path="/"              element={<Home />} />
          <Route path="/how-it-works"  element={<HowItWorks />} />
          <Route path="/technology"    element={<Technology />} />
          <Route path="/safety"        element={<Safety />} />
          <Route path="/types"         element={<Types />} />
          <Route path="/contact"       element={<Contact />} />
          <Route path="/privacy"       element={<Privacy />} />
          <Route path="/terms"         element={<Terms />} />
          <Route path="/accessibility" element={<Accessibility />} />
          <Route path="/newsletter"    element={<Newsletter />} />
          <Route path="/brochure"      element={<Brochure />} />

          {/* 404 */}
          <Route path="*" element={
            <div style={{ minHeight:'70vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:20, paddingTop:66, textAlign:'center', padding:'80px 24px' }}>
              <div style={{ width:80, height:80, borderRadius:'50%', background:'var(--primary-pale)', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--primary)' }}>
                <LuPlane size={40}/>
              </div>
              <h1 style={{ fontFamily:'var(--font-display)', fontSize:'2rem', fontWeight:800, color:'var(--text)' }}>Page Not Found</h1>
              <p style={{ color:'var(--text-muted)', maxWidth:380, lineHeight:1.7 }}>
                This page seems to have gone missing — like a misrouted bag. Let's get you back on track.
              </p>
              <div style={{ display:'flex', gap:12, flexWrap:'wrap', justifyContent:'center' }}>
                <a href="/" className="btn btn-primary">← Back to Home</a>
                <a href="/contact" className="btn btn-outline">Contact Support</a>
              </div>
            </div>
          } />
        </Routes>
      </main>
      {!isAdminRoute && <Footer />}
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AdminToastProvider>
        <AppContent />
      </AdminToastProvider>
    </BrowserRouter>
  );
}

export default App;
